/**
 * Session bridge — a module-level pointer to the current CSRF token.
 *
 * The HTTP client (lib/api.ts) must attach the CSRF token to every mutation,
 * and `useQuery({ queryFn })` closures cannot call `useAuth()` (rules of
 * hooks). This bridge gives lib/api a stable getter without prop-drilling or
 * context plumbing through TanStack Query's queryFn signature.
 *
 * The AuthProvider writes here on every session change; lib/api reads.
 *
 * Note what is NOT here any more: the access token. It lives in an `HttpOnly`
 * cookie the browser attaches on its own, which no script — ours or an
 * attacker's — can read. The CSRF token is the one credential-adjacent value
 * JavaScript still needs to hold, precisely because the cookie is ambient.
 */

import type { Session } from "./types";

let csrfToken: string | null = null;
let authenticated = false;
let refreshFn: (() => Promise<Session>) | null = null;
let resyncFn: (() => Promise<Session>) | null = null;
let inFlightRefresh: Promise<Session> | null = null;
let inFlightResync: Promise<Session> | null = null;
let denialFn: ((code: string, message: string, details?: Record<string, unknown>) => void) | null =
  null;

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * Whether the API last told us we hold a session.
 *
 * This is what lets the HTTP client tell "our access cookie lapsed, refresh it"
 * apart from "we were never signed in" — two states that arrive as the *same*
 * 401 UNAUTHENTICATED, because a cookie the browser has dropped is
 * indistinguishable from one that never existed. Without it, an anonymous
 * visitor's every 401 would start a pointless refresh.
 */
export function isAuthenticated(): boolean {
  return authenticated;
}

export function setAuthenticated(value: boolean): void {
  authenticated = value;
}

/**
 * Trigger a session refresh via the AuthProvider. Called by the HTTP client on
 * a 401 that looks like a lapsed access cookie, before retrying the request once.
 *
 * Single-flight: a cold page load fires several queries at once, and with an
 * expired access token every one of them would 401 and start its own refresh.
 * That is a stampede against Cognito for one user's one expiry, so concurrent
 * callers share the first attempt's promise.
 */
export async function refreshSessionViaBridge(): Promise<Session> {
  if (!refreshFn) {
    throw new Error("Session bridge: AuthProvider has not registered a refresh handler");
  }
  if (inFlightRefresh) return inFlightRefresh;
  const attempt = refreshFn().finally(() => {
    inFlightRefresh = null;
  });
  inFlightRefresh = attempt;
  return attempt;
}

export function setRefreshFn(fn: (() => Promise<Session>) | null): void {
  refreshFn = fn;
}

export function setResyncFn(fn: (() => Promise<Session>) | null): void {
  resyncFn = fn;
}

/**
 * Re-read the session to recover a CSRF token that another tab's refresh rotated.
 *
 * Single-flighted for the same reason as refresh: several queued mutations can
 * each meet the stale token, and they need one re-read between them rather than
 * one apiece.
 */
export async function resyncSessionViaBridge(): Promise<Session> {
  if (!resyncFn) {
    throw new Error("Session bridge: AuthProvider has not registered a resync handler");
  }
  if (inFlightResync) return inFlightResync;
  const attempt = resyncFn().finally(() => {
    inFlightResync = null;
  });
  inFlightResync = attempt;
  return attempt;
}

/**
 * Report a settled refusal of the *session* seen on any route.
 *
 * The refusals in `ACCOUNT_DENIAL_CODES` are not about the request that met
 * them - they say the caller may not be here at all - but they arrive on whatever
 * route happened to run next, which is usually an ordinary data query. Left
 * there, each page renders its own error and the user stays inside the protected
 * UI: `/me` holds `staleTime: Infinity`, so nothing re-asks the question that
 * would produce the screen they should be seeing.
 *
 * This is the same reason the bridge exists at all. A `queryFn` closure cannot
 * call `useAuth`, so the transport needs a module-level way to tell the auth
 * layer what it just saw.
 *
 * Best-effort by design: with no handler registered - before the provider mounts,
 * or in a test that renders neither - the refusal simply surfaces as the caller's
 * own error, which is what happened before this existed.
 *
 * `details` carries the refusal's payload, which one of these needs rather than
 * merely benefits from: `ORGANIZATION_SELECTION_REQUIRED` is a question, and its
 * `candidates` are the answers the picker offers. Without them the auth layer
 * would know a choice is wanted and have nothing to choose between.
 */
export function reportSessionDenialViaBridge(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): void {
  denialFn?.(code, message, details);
}

export function setDenialFn(
  fn: ((code: string, message: string, details?: Record<string, unknown>) => void) | null,
): void {
  denialFn = fn;
}

/** Test seam — drops any shared in-flight attempt between cases. */
export function resetSessionBridge(): void {
  csrfToken = null;
  authenticated = false;
  refreshFn = null;
  resyncFn = null;
  inFlightRefresh = null;
  inFlightResync = null;
  denialFn = null;
}
